import { Box } from "@mui/material";
import type { TFunction } from "i18next";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type SystemChipItem, SystemChips } from "@/ui/Clickable/TabsAndChips/SystemChips/SystemChips";
import { ShiftWindowIcon } from "@/ui/Icons/ShiftWindowIcon";
import { formatWindowHours, REMOVE_STAFF_ALL_FILTER, windowShiftIndex } from "./removeStaffUtils";
import { REMOVE_STAFF_OTHER_WINDOW_KEY, type RemoveStaffWindow } from "./removeStaffWindows";

type Props = {
	windows: readonly RemoveStaffWindow[];
	otherCount: number;
	/** Every shift on the date — the "All shifts" count. */
	totalCount: number;
	activeKey: string;
	/** Picks the active pill hides, per window key: those pills say "2 picked" so no pick is out of sight unmarked. */
	hiddenPickCounts: ReadonlyMap<string, number>;
	onChange: (key: string) => void;
};

/** A window key ("07:00-15:00") as a test-id segment ("0700-1500"). */
export const filterTestIdSegment = (key: string): string => key.replace(/:/g, "");

/** A pill's "2 picked" note (and its accessible name with it) — only when the active pill hides picks of its window. */
const withHiddenPicks = (item: SystemChipItem<string>, hiddenPickCounts: ReadonlyMap<string, number>, t: TFunction): SystemChipItem<string> => {
	const hidden = hiddenPickCounts.get(item.value);
	if (hidden === undefined) return item;
	const note = t("remove.staff.filter.picked", { count: hidden });
	return { ...item, note, ariaLabel: item.ariaLabel ? `${item.ariaLabel}, ${note}` : undefined };
};

const windowItem = (window: RemoveStaffWindow, t: TFunction): SystemChipItem<string> => {
	const hours = formatWindowHours(window);
	return {
		value: window.key,
		label: hours,
		// The pill's aria-label names the hours; the sun / moon beside them is decoration.
		icon: <ShiftWindowIcon shiftIndex={windowShiftIndex(window)} />,
		count: window.shiftCount,
		// A staffed window nobody works on this date: shown (it's where the gap is) but there is no one to remove.
		disabled: window.shiftCount === 0,
		ariaLabel: t("remove.staff.filter.window.aria", { hours, count: window.shiftCount }),
	};
};

const otherItem = (otherCount: number, t: TFunction): SystemChipItem<string> => ({
	value: REMOVE_STAFF_OTHER_WINDOW_KEY,
	label: t("remove.staff.window.other"),
	count: otherCount,
	ariaLabel: t("remove.staff.filter.other.aria", { count: otherCount }),
});

const useFilterItems = ({
	windows,
	otherCount,
	totalCount,
	hiddenPickCounts,
}: Pick<Props, "windows" | "otherCount" | "totalCount" | "hiddenPickCounts">): SystemChipItem<string>[] => {
	const { t } = useTranslation();
	return useMemo(() => {
		const all: SystemChipItem<string> = {
			value: REMOVE_STAFF_ALL_FILTER,
			label: t("remove.staff.filter.all"),
			count: totalCount,
			ariaLabel: t("remove.staff.filter.all.aria", { count: totalCount }),
		};
		const other = otherCount > 0 ? [otherItem(otherCount, t)] : [];
		return [all, ...windows.map((window) => windowItem(window, t)), ...other].map((item) => withHiddenPicks(item, hiddenPickCounts, t));
	}, [windows, otherCount, totalCount, hiddenPickCounts, t]);
};

/**
 * "All shifts 9 · ☀ 7:00 AM–3:00 PM 6 · ☀ 3:00 PM–11:00 PM 3 · ☾ 11:00 PM–7:00 AM 0": one pill per time window
 * on the date, single choice. A pill narrows what the list SHOWS; staff already ticked in another window stay
 * ticked, and that window's pill says so ("• 2 picked"). A window with nobody on it stays visible (that's the
 * gap) but can't be picked.
 */
export const RemoveStaffWindowFilter: FC<Props> = ({ windows, otherCount, totalCount, activeKey, hiddenPickCounts, onChange }) => {
	const { t } = useTranslation();
	const items = useFilterItems({ windows, otherCount, totalCount, hiddenPickCounts });
	const selectedItems = useMemo(() => items.filter((item) => item.value === activeKey), [items, activeKey]);
	return (
		<Box role="group" aria-label={t("remove.staff.filter.label")} sx={{ display: "flex", flex: "0 1 auto", minWidth: 0, marginInlineEnd: "auto" }}>
			<SystemChips<string>
				variant="outlined-brand"
				items={items}
				selectedItems={selectedItems}
				// Single choice: re-clicking the live pill keeps it (there is always one filter on).
				onChange={(item, selected) => selected && onChange(item.value)}
				forceFitContent
				rootSx={{ justifyContent: "flex-start" }}
				dataTestId="remove-staff-filters"
				getItemDataTestId={(item) => `remove-staff-filter-${filterTestIdSegment(item.value)}`}
			/>
		</Box>
	);
};
